import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import Loading from "../components/Loading";
import TicketFilters, { type SortKey } from "../components/TicketFilters";
import TicketListing from "../components/TicketListing";
import VenueMap from "../components/VenueMap";
import { priceTier, type Tier } from "../lib/pricing";
import { formatEventDate, gradientFor, styleForCategory } from "../lib/visual";
import type { EventDetail, TicketGroup } from "../types/api";
import styles from "./EventPage.module.css";

export default function EventPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [groups, setGroups] = useState<TicketGroup[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Map / listing interaction state
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);

  // Filters
  const [qty, setQty] = useState<number | "any">("any");
  const [sort, setSort] = useState<SortKey>("best");

  useEffect(() => {
    if (!id) return;
    setError(null);
    setSelectedSection(null);
    setHoveredSection(null);
    Promise.all([api.getEvent(id), api.getEventTickets(id)])
      .then(([d, t]) => {
        setDetail(d);
        setGroups(t.ticketGroups);
      })
      .catch((e: Error) => setError(e.message));
  }, [id]);

  const allPrices = useMemo(
    () => groups.map((g) => g.pricePerTicket),
    [groups],
  );

  const tierByGroup = useMemo<Record<string, Tier>>(() => {
    const out: Record<string, Tier> = {};
    for (const g of groups) {
      out[g.id] = priceTier(g.pricePerTicket, allPrices);
    }
    return out;
  }, [groups, allPrices]);

  const visibleGroups = useMemo(() => {
    let list = groups;
    if (selectedSection) {
      list = list.filter((g) => g.section === selectedSection);
    }
    if (qty !== "any") {
      list = list.filter((g) => g.splits.includes(qty));
    }
    const sorted = [...list];
    if (sort === "price-asc") {
      sorted.sort((a, b) => a.pricePerTicket - b.pricePerTicket);
    } else if (sort === "price-desc") {
      sorted.sort((a, b) => b.pricePerTicket - a.pricePerTicket);
    } else {
      // "best" — premium tier first, then descending tier, then cheaper-within-tier
      sorted.sort((a, b) => {
        const ta = tierByGroup[a.id] ?? 0;
        const tb = tierByGroup[b.id] ?? 0;
        if (ta !== tb) return tb - ta;
        return a.pricePerTicket - b.pricePerTicket;
      });
    }
    return sorted;
  }, [groups, selectedSection, qty, sort, tierByGroup]);

  if (error) {
    return (
      <div className="container">
        <div className="empty">Failed to load: {error}</div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="container">
        <Loading />
      </div>
    );
  }

  const { event, performers, venue } = detail;
  const date = formatEventDate(event.datetime);
  const cat = styleForCategory(event.categoryId);
  const gradient = gradientFor(event.id, event.categoryId);

  function handleAdd(group: TicketGroup, quantity: number) {
    const params = new URLSearchParams({
      event: event.id,
      group: group.id,
      qty: String(quantity),
    });
    navigate(`/checkout?${params.toString()}`);
  }

  return (
    <div className={styles.page}>
      {/* Slim hero banner */}
      <div className={styles.banner} style={{ background: gradient }}>
        <span className={styles.glyph} aria-hidden>
          {cat.glyph}
        </span>
        <div className={styles.bannerOverlay} />
        <div className={`container ${styles.bannerContent}`}>
          <div className={styles.kicker}>
            <span className={styles.categoryPill}>{cat.shortLabel}</span>
            {performers.length > 0 && (
              <span className={styles.performerLine}>
                {performers.map((p) => p.name).join(", ")}
              </span>
            )}
          </div>
          <h1 className={styles.title}>{event.name}</h1>
          <div className={styles.metaInline}>
            <span>{date.full}</span>
            <span className={styles.dot}>·</span>
            <span>{date.time}</span>
            <span className={styles.dot}>·</span>
            <span>
              {venue.name}, {venue.city}
            </span>
          </div>
        </div>
      </div>

      <div className={`container ${styles.body}`}>
        <hr className={styles.divider} />
        <div className={styles.layout}>
          {/* Left pane: map (sticky on desktop) */}
          <aside className={styles.mapPane}>
            <VenueMap
              groups={groups}
              selectedSection={selectedSection}
              hoveredSection={hoveredSection}
              onSelectSection={setSelectedSection}
              onHoverSection={setHoveredSection}
            />
            <div className={styles.helper}>
              Tap a section to filter listings. Hover a listing to highlight on
              the map.
            </div>
          </aside>

          {/* Right pane: filters + listings */}
          <section className={styles.listPane}>
            <h2 className={styles.listHead}>
              {selectedSection
                ? `Listings in ${selectedSection}`
                : "All listings"}
            </h2>

            <TicketFilters
              count={visibleGroups.length}
              total={groups.length}
              qty={qty}
              setQty={setQty}
              sort={sort}
              setSort={setSort}
            />

            {visibleGroups.length === 0 ? (
              <div className={styles.emptyList}>
                <div>No listings match these filters.</div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setQty("any");
                    setSelectedSection(null);
                  }}
                >
                  Reset filters
                </button>
              </div>
            ) : (
              <div className={styles.list}>
                {visibleGroups.map((g) => (
                  <TicketListing
                    key={g.id}
                    group={g}
                    tier={tierByGroup[g.id] ?? 0}
                    isHighlighted={hoveredSection === g.section}
                    onAdd={handleAdd}
                    onHover={setHoveredSection}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
